import React, { useEffect, useState, useCallback } from "react";
import Table from "@components/table";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Order } from "@apis/index";
import AccessDenied from "@components/access-denied";
import { useOrderEvents } from "@hooks/useOrderEvents";
import { useModal } from "@context/ModalContext";
import {
  Container,
  EmptyIcon,
  EmptyState,
  EmptyText,
  EmptyTitle,
  HeaderContainer,
  HeaderSubtext,
  HeaderTitle,
  IconWrapper,
  StatCard,
  StatsContainer,
  StatTitle,
  StatValue,
  Subtitle,
  Title,
  TitleSection,
  OrderIdLink,
  DateTimeWrapper,
  DateText,
  TimeText,
  OrderStatusWrapper,
  StatusPill,
  DeliveryType,
  ActionsWrapper,
  ActionButton,
  ProfileCard,
  ProfileAvatar,
  ProfileAvatarPlaceholder,
  ProfileDetails,
  ProfileName,
  ProfileSubtext,
  ProfilePhone,
  SerialBadge,
  QuantityBadge,
  AmountDisplay,
  AmountValue,
  PaymentBadge,
  LoadingContainer,
  LoadingSpinner,
  LoadingText,
  FilterWrapper,
  FilterSelect,
  FilterButton,
} from "./style";
import FilterSidebar, { FilterState } from "./FilterSidebar";
import { Helper } from "@utils/index";
import OrderStatusCards from "./OrderStatusCards";
import { useSelector } from "react-redux";
import { RootState } from "@store/index";
import useConfig from "@hooks/useConfig";
import { useGeneralSettings } from "@hooks/useGeneralSettings";
import Invoice from "./individual-order-details/invoice";
import usePermission from "@hooks/usePermission";

type OrderStatus =
  | "All"
  | "Order Placed"
  | "Order Confirmed"
  | "Order Processing"
  | "Delivery Assigned"
  | "Ready for Pickup"
  | "Order Picked Up"
  | "Out for Delivery"
  | "Near Customer"
  | "Delivered"
  | "Cancelled"
  | "Rejected"
  | "Prescription Requested"
  | "Prescription Accepted"
  | "Creating Invoice"
  | "Invoice Shared"
  | "Invoice Accepted"
  | "Prescription Rejected";

const statusMapping: Record<OrderStatus, string | undefined> = {
  All: undefined,
  "Order Placed": "Order Placed",
  "Order Confirmed": "Order Confirmed",
  "Order Processing": "Order Processing",
  "Delivery Assigned": "Delivery Assigned",
  "Ready for Pickup": "Ready for Pickup",
  "Order Picked Up": "Order Picked Up",
  "Out for Delivery": "Out for Delivery",
  "Near Customer": "Near Customer",
  Delivered: "Delivered",
  Cancelled: "Cancelled",
  Rejected: "Rejected",
  "Prescription Requested": "Prescription Requested",
  "Prescription Accepted": "Prescription Accepted",
  "Creating Invoice": "Creating Invoice",
  "Invoice Shared": "Invoice Shared",
  "Invoice Accepted": "Invoice Accepted",
  "Prescription Rejected": "Prescription Rejected",
};

const statusConfig: Record<
  OrderStatus,
  { label: string; color: string; backgroundColor: string; icon: string }
> = {
  All: {
    label: "All Orders",
    color: "#6b7280",
    backgroundColor: "#f3f4f6",
    icon: "📦",
  },
  "Order Placed": {
    label: "Order Placed",
    color: "#92400e",
    backgroundColor: "#fef3c7",
    icon: "📝",
  },
  "Order Confirmed": {
    label: "Order Confirmed",
    color: "#065f46",
    backgroundColor: "#d1fae5",
    icon: "✅",
  },
  "Order Processing": {
    label: "Order Processing",
    color: "#1e40af",
    backgroundColor: "#dbeafe",
    icon: "📦",
  },
  "Delivery Assigned": {
    label: "Delivery Assigned",
    color: "#7c3aed",
    backgroundColor: "#ede9fe",
    icon: "🚴",
  },
  "Ready for Pickup": {
    label: "Ready for Pickup",
    color: "#0891b2",
    backgroundColor: "#cffafe",
    icon: "📋",
  },
  "Order Picked Up": {
    label: "Order Picked Up",
    color: "#ea580c",
    backgroundColor: "#ffedd5",
    icon: "�",
  },
  "Out for Delivery": {
    label: "Out for Delivery",
    color: "#7c2d12",
    backgroundColor: "#fed7aa",
    icon: "�",
  },
  "Near Customer": {
    label: "Near Customer",
    color: "#15803d",
    backgroundColor: "#bbf7d0",
    icon: "📍",
  },
  Delivered: {
    label: "Delivered",
    color: "#047857",
    backgroundColor: "#dcfce7",
    icon: "✓",
  },
  Cancelled: {
    label: "Cancelled",
    color: "#991b1b",
    backgroundColor: "#fee2e2",
    icon: "❌",
  },
  Rejected: {
    label: "Rejected",
    color: "#9f1239",
    backgroundColor: "#ffe4e6",
    icon: "🚫",
  },
  "Prescription Requested": {
    label: "Prescription Requested",
    color: "#00695c",
    backgroundColor: "#e0f2f1",
    icon: "📄",
  },
  "Prescription Accepted": {
    label: "Prescription Accepted",
    color: "#0f766e",
    backgroundColor: "#ccfbf1",
    icon: "👍",
  },
  "Creating Invoice": {
    label: "Creating Invoice",
    color: "#9d174d",
    backgroundColor: "#fce7f3",
    icon: "🧾",
  },
  "Invoice Shared": {
    label: "Invoice Shared",
    color: "#6a1b9a",
    backgroundColor: "#f3e5f5",
    icon: "📤",
  },
  "Invoice Accepted": {
    label: "Invoice Accepted",
    color: "#166534",
    backgroundColor: "#dcfce7",
    icon: "✅",
  },
  "Prescription Rejected": {
    label: "Prescription Rejected",
    color: "#991b1b",
    backgroundColor: "#fee2e2",
    icon: "👎",
  },
};

const OrderTable = ({
  type,
  initialFilters,
}: {
  type: OrderStatus;
  initialFilters?: Partial<FilterState>;
}) => {
  const user = Helper.getUser();
  const { settings } = useGeneralSettings();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: modules } = useSelector((state: RootState) => state.modules);
  // Module filter lives in the table itself. Defaults to "" (All Modules) so the
  // table shows every order and matches the status cards, which also count across
  // all modules unless a module is explicitly chosen. A `moduleId` query param
  // (deep link) is honored, but we no longer silently filter by the globally
  // selected module from localStorage, which previously hid orders.
  const [moduleId, setModuleId] = useState<string>(
    searchParams.get("moduleId") || "",
  );
  const { showModal } = useModal();
  const { hasPermission } = usePermission();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentStatus, setCurrentStatus] = useState<OrderStatus>(type);
  const [filters, setFilters] = useState<FilterState>({
    paymentStatus: "",
    datePreset: "",
    startDate: "",
    endDate: "",
    storeId: user.role === "admin" ? "" : user.id,
    ...initialFilters,
  });

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState("");

  const [stats, setStats] = useState({
    totalAmount: 0,
    averageAmount: 0,
    totalItems: 0,
    averageItems: 0,
  });

  const statusInfo = statusConfig[currentStatus] ||
    statusConfig[type] || {
      label: type,
      color: "#6b7280",
      backgroundColor: "#f3f4f6",
      icon: "📋",
    };
  const prefix = window.location.pathname.startsWith("/admin")
    ? "/admin"
    : "/store";

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const backendStatus = statusMapping[currentStatus];
      const params: any = { limit: 10, page };

      if (backendStatus) {
        params.status = backendStatus;
      }

      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.paymentStatus && filters.paymentStatus !== "All") {
        params.paymentStatus = filters.paymentStatus;
      }
      if (filters.storeId) {
        params.storeId = filters.storeId;
      }
      if (filters.customerId) {
        params.createdBy = filters.customerId;
      }
      if (filters.deliveryBoyId) {
        params.deliveryBoyId = filters.deliveryBoyId;
      }
      if (search) {
        params.search = search;
      }
      if (moduleId) {
        params.moduleId = moduleId;
      }
      const res = await Order.list({
        params,
      });
      // The API returns { items: [], page: ..., totalItems: ... }
      // Or sometimes directly array depending on implementation?
      // service.ts returns { items: ..., ... }
      const orders = res?.items || [];
      setData(orders);
      setTotalPages(res.totalPages || 1);
      setTotalItems(res.totalItems || 0);

      setStats({
        totalAmount: res.stats?.totalAmount || 0,
        averageAmount: res.stats?.averageAmount || 0,
        totalItems: res.stats?.totalItems || 0,
        averageItems: res.stats?.averageItems || 0,
      });
    } catch (error: any) {
      console.error("Failed to fetch orders", error);
      setError(error.message || "Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  }, [currentStatus, filters, page, search, moduleId]);

  // Use enhanced order events hook for real-time updates
  // Toasts and sounds disabled - handled by NewOrderModal banner
  useOrderEvents({
    onNewOrder: fetchData,
    onOrderUpdated: fetchData,
    onDeliveryAssigned: fetchData,
    onStatusChange: fetchData,
    playSound: false,
    showToasts: false,
  });

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    setCurrentStatus(type);
  }, [type]);

  // Socket events are now handled by useOrderEvents hook above

  // Helper to get status key from backend status string
  const getStatusKey = (backendStatus: string): OrderStatus => {
    // Since we're now using the actual status names, just return as is if it matches
    const validStatuses: OrderStatus[] = [
      "Order Placed",
      "Order Confirmed",
      "Order Processing",
      "Delivery Assigned",
      "Ready for Pickup",
      "Order Picked Up",
      "Out for Delivery",
      "Near Customer",
      "Delivered",
      "Cancelled",
      "Rejected",
      "Prescription Requested",
      "Prescription Accepted",
      "Creating Invoice",
      "Invoice Shared",
      "Invoice Accepted",
      "Prescription Rejected",
    ];

    if (validStatuses.includes(backendStatus as OrderStatus)) {
      return backendStatus as OrderStatus;
    }

    // Fallback to Order Placed if status not recognized
    return "Order Placed";
  };
  const { bucketUrl } = useConfig();
  const columns = [
    {
      title: "Order Id",
      key: "orderId",
      width: "7%",
      render: (row: any) => (
        <OrderIdLink
          onClick={(e) => {
            e.stopPropagation();
            navigate(`${prefix}/orders/details/${row.id}`);
          }}
        >
          #{row.id}
        </OrderIdLink>
      ),
    },
    {
      title: "Tracking Id",
      key: "trackingId",
      width: "7%",
      render: (row: any) => (
        <OrderIdLink
          onClick={(e) => {
            e.stopPropagation();
            navigate(`${prefix}/orders/details/${row.id}`);
          }}
        >
          #{row.trackingId}
        </OrderIdLink>
      ),
    },
    {
      title: "Order Date",
      key: "orderDate",
      width: "10%",
      render: (row: any) => {
        const date = new Date(row.createdAt);
        return (
          <DateTimeWrapper>
            <DateText>
              {date.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </DateText>
            <TimeText>
              {date.toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "numeric",
                hour12: true,
              })}
            </TimeText>
          </DateTimeWrapper>
        );
      },
    },
    {
      title: "Customer",
      key: "customerInfo",
      width: "15%",
      render: (row: any) => {
        const customerName =
          row.customer?.name || row.shippingInfos?.fullName || "Guest";
        const customerPhone =
          row.customer?.phoneNo || row.shippingInfos?.phone || "";
        const customerAvatar = row.customer?.avatar;
        const initials = customerName.charAt(0).toUpperCase();

        return (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <ProfileCard>
              {customerAvatar ? (
                <ProfileAvatar
                  src={`${bucketUrl}/${customerAvatar}`}
                  alt={customerName}
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                    e.currentTarget.nextElementSibling?.removeAttribute(
                      "style",
                    );
                  }}
                />
              ) : null}
              <ProfileAvatarPlaceholder
                variant="customer"
                style={customerAvatar ? { display: "none" } : undefined}
              >
                {initials}
              </ProfileAvatarPlaceholder>
              <ProfileDetails>
                <ProfileName>{customerName}</ProfileName>
                {customerPhone && <ProfilePhone> {customerPhone}</ProfilePhone>}
              </ProfileDetails>
            </ProfileCard>
          </div>
        );
      },
    },
    ...(user.role === "admin"
      ? [
          {
            title: "Store",
            key: "store",
            width: "16%",
            render: (row: any) => {
              // Get store from items[0].product.store or from row.storex
              const store = row.items?.[0]?.product?.store || row.store;
              if (!store) {
                return (
                  <ProfileName style={{ color: "#9ca3af" }}>—</ProfileName>
                );
              }

              const storeName =
                store.name || store.storeName || "Unknown Store";
              const storePhone = store.phoneNo || store.phone || "";
              const storeEmail = store.email || "";
              const storeAvatar = store.avatar || store.image;
              const initials = storeName.charAt(0).toUpperCase();

              return (
                <div
                  style={{ display: "flex", justifyContent: "center" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(
                      `/admin/users/store/store-lists/view/${store.userId}?module=Food`,
                    );
                  }}
                >
                  <ProfileCard>
                    {storeAvatar ? (
                      <ProfileAvatar
                        src={`${bucketUrl}/${storeAvatar}`}
                        alt={storeName}
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                          e.currentTarget.nextElementSibling?.removeAttribute(
                            "style",
                          );
                        }}
                      />
                    ) : null}
                    <ProfileAvatarPlaceholder
                      variant="store"
                      style={storeAvatar ? { display: "none" } : undefined}
                    >
                      {initials}
                    </ProfileAvatarPlaceholder>
                    <ProfileDetails>
                      <ProfileName clickable>{storeName}</ProfileName>
                      {storePhone && (
                        <ProfileSubtext> {storePhone}</ProfileSubtext>
                      )}
                    </ProfileDetails>
                  </ProfileCard>
                </div>
              );
            },
          },
        ]
      : []),

    {
      title: "Items",
      key: "itemQuantity",
      width: "6%",
      render: (row: any) => (
        <QuantityBadge>{row.items?.length || 0}</QuantityBadge>
      ),
    },
    {
      title: "Amount",
      key: "totalAmount",
      width: "10%",
      render: (row: any) => {
        const isPaid =
          row.paymentStatus === "paid" ||
          row.paymentStatus === "Paid" ||
          row.paymentStatus === "success" ||
          row.transaction?.status === "paid" ||
          row.transaction?.status === "success";
        return (
          <AmountDisplay>
            <AmountValue>
              {Helper.formatCurrency(row.actualAmount, settings)}
            </AmountValue>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                background: isPaid ? "#dcfce7" : "#fee2e2",
                padding: "2px 6px",
                borderRadius: "4px",
                marginTop: "4px",
                width: "fit-content",
              }}
            >
              <PaymentBadge
                isPaid={isPaid}
                style={{ color: isPaid ? "#166534" : "#991b1b" }}
              >
                {isPaid ? " Paid" : " Unpaid"}
              </PaymentBadge>
            </div>
          </AmountDisplay>
        );
      },
    },
    {
      title: "Status",
      key: "orderStatus",
      width: "12%",
      render: (row: any) => {
        const statusKey = getStatusKey(row.status) || currentStatus;
        const deliveryType =
          row.deliveryType || row.shippingMethod || "Home Delivery";
        return (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <OrderStatusWrapper>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <StatusPill status={statusKey}>
                  {statusConfig[statusKey]?.label || row.status}
                </StatusPill>
              </div>
              <DeliveryType>🚚 {deliveryType}</DeliveryType>
            </OrderStatusWrapper>
          </div>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: "10%",
      render: (row: any) => (
        <ActionsWrapper>
          {hasPermission("orders", "Order Details") && (
            <ActionButton
              variant="view"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`${prefix}/orders/details/${row.id}`);
              }}
              title="View Order"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            </ActionButton>
          )}
          {hasPermission("orders", "Invoice") && (
            <ActionButton
              variant="print"
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  const fullOrder = await Order.find(row.id);
                  showModal(<Invoice order={fullOrder} />, { width: "480px" });
                } catch (err) {
                  console.error("Failed to load order for invoice", err);
                }
              }}
              title="View Receipt"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1-2-1z" />
                <line x1="8" y1="8" x2="16" y2="8" />
                <line x1="8" y1="12" x2="16" y2="12" />
                <line x1="8" y1="16" x2="12" y2="16" />
              </svg>
            </ActionButton>
          )}
        </ActionsWrapper>
      ),
    },
  ];

  const { settings: currencySettings } = useGeneralSettings();
  return (
    <Container>
      <OrderStatusCards
        storeId={Number(filters.storeId)}
        customerId={filters.customerId}
        deliveryBoyId={filters.deliveryBoyId}
        moduleId={moduleId ? Number(moduleId) : undefined}
      />

      {type === "All" && (
        <StatsContainer>
          <StatCard>
            <StatTitle>Average Order Value</StatTitle>
            <StatValue>
              {Helper.formatCurrency(
                stats.averageAmount.toFixed(0),
                currencySettings,
              )}
            </StatValue>
          </StatCard>
          <StatCard>
            <StatTitle>Total Order Value</StatTitle>
            <StatValue>
              {Helper.formatCurrency(
                stats.totalAmount.toFixed(0),
                currencySettings,
              )}
            </StatValue>
          </StatCard>
          <StatCard>
            <StatTitle>Items per Order</StatTitle>
            <StatValue>{stats.averageItems.toFixed(1)}</StatValue>
          </StatCard>
          <StatCard>
            <StatTitle>Total Orders</StatTitle>
            <StatValue>{totalItems}</StatValue>
          </StatCard>
        </StatsContainer>
      )}
      <HeaderContainer>
        <TitleSection>
          <IconWrapper color={`${statusInfo.backgroundColor}40`}>
            <span>{statusInfo.icon}</span>
          </IconWrapper>
          <div>
            <Title>{statusInfo.label} Orders</Title>
            <Subtitle>
              Manage{" "}
              {currentStatus === "All"
                ? "all"
                : `all ${currentStatus.toLowerCase()}`}{" "}
              orders in one place
            </Subtitle>
          </div>
        </TitleSection>

        <FilterWrapper>
          <FilterSelect
            value={moduleId}
            onChange={(e) => {
              setModuleId(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All Modules</option>
            {modules?.map((m: any) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </FilterSelect>
          <FilterSelect
            value={currentStatus}
            onChange={(e) => {
              setCurrentStatus(e.target.value as OrderStatus);
              setFilters((prev) => ({
                ...prev,
                paymentStatus: "",
                datePreset: "",
                startDate: "",
                endDate: "",
                storeId: user.role === "admin" ? "" : user.id,
                ...initialFilters,
              }));
              setPage(1);
            }}
          >
            {Object.keys(statusConfig).map((status) => (
              <option key={status} value={status}>
                {statusConfig[status as keyof typeof statusConfig].label}
              </option>
            ))}
          </FilterSelect>
          <FilterButton
            onClick={() =>
              showModal(
                <FilterSidebar
                  initialFilters={filters}
                  onApply={(newFilters) => {
                    setFilters(newFilters);
                  }}
                />,
                { width: 720, padding: "0", height: "100%" },
              )
            }
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />
            </svg>
            Filter
          </FilterButton>
        </FilterWrapper>

        <div style={{ textAlign: "right" }}>
          <HeaderTitle>
            {totalItems} order{totalItems !== 1 ? "s" : ""}
          </HeaderTitle>
          <HeaderSubtext>
            Total: {Helper.formatCurrency(stats.totalAmount, currencySettings)}
          </HeaderSubtext>
        </div>
      </HeaderContainer>
      {error ? (
        <AccessDenied message={error} onRetry={fetchData} />
      ) : loading ? (
        <LoadingContainer>
          <LoadingSpinner />
          <LoadingText>Loading orders...</LoadingText>
        </LoadingContainer>
      ) : data.length > 0 ? (
        <>
          <Table
            columns={columns}
            data={data}
            totalItems={totalItems}
            totalPages={totalPages}
            itemsPerPage={10}
            pageControlled={page}
            onPageChange={setPage}
            hasSearchbar={true}
            onSearchChange={setSearch}
            onRowClick={(row) => navigate(`${prefix}/orders/details/${row.id}`)}
          />
        </>
      ) : (
        <EmptyState>
          <EmptyIcon>📦</EmptyIcon>
          <EmptyTitle>
            No {currentStatus === "All" ? "" : currentStatus} orders found
          </EmptyTitle>
          <EmptyText>
            There are currently no orders
            {currentStatus !== "All" ? ` with ${currentStatus} status` : ""}.
          </EmptyText>
        </EmptyState>
      )}
    </Container>
  );
};

export default OrderTable;
